(() => {
  'use strict';

  if (window.NLAPiecesMotion) return;
  let instance = 0;
  const rememberedColors = new Map();
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);
  const json = value => JSON.stringify(value).replace(/</g, '\\u003c');
  const own = (object, key) => object && Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null;
  const read = (node, fallback) => {
    try { return JSON.parse(node?.textContent || '') || fallback; } catch { return fallback; }
  };
  const imageURL = value => {
    const candidate = typeof value === 'string' ? value : value?.url;
    if (!candidate) return '';
    try {
      const source = new URL(candidate, location.href);
      return ['https:', 'http:'].includes(source.protocol) ? source.href : '';
    } catch { return ''; }
  };
  const imageAlt = value => typeof value === 'object' ? value?.alt || '' : '';
  const variants = product => (product.variants || []).filter(variant =>
    variant.id != null && Number.isInteger(Number(variant.price)) && Number(variant.price) >= 0);
  const colors = product => product.colors?.length ? product.colors :
    [...new Set(variants(product).map(variant => String(variant.color || '')))].map(name => ({ name, images: [] }));
  const summary = product => {
    const node = document.createElement('template');
    node.innerHTML = String(product.descriptionHtml || '');
    const paragraph = node.content.querySelector('p');
    return (paragraph?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 260);
  };

  function mount(rack, options = {}) {
    if (!rack || !rack.isConnected) return () => {};
    const items = Array.isArray(options.items) ? [...options.items] : [];
    const data = read(document.querySelector('#lineup-data'), {});
    const sourceBindings = options.bindings || window.NLAPiecesMotionBindings ||
      read(document.querySelector('[data-pieces-motion-bindings]'), {});
    const currency = data.currency || 'CHF';
    const formatPrice = amount => Number.isFinite(Number(amount))
      ? new Intl.NumberFormat('de-CH', { style: 'currency', currency }).format(Number(amount) / 100)
      : 'Preis wird bestätigt';
    const id = `nla-pieces-motion-${++instance}`;
    const mapped = {};
    const byId = new Map(items.map(product => [String(product.id), product]));
    let preferences = {};
    try {
      const saved = JSON.parse(localStorage.getItem('lineup:v3:colors') || '{}');
      if (saved && typeof saved === 'object' && !Array.isArray(saved)) preferences = saved;
    } catch {}

    function colorChoice(product, color) {
      const name = String(color.name || '');
      const entry = own(own(sourceBindings, String(product.id)), name);
      const faces = color.hangerViews || own(product.garmentFaces, name) || {};
      let side = entry?.printSide === 'back' || (!entry && faces.printSide === 'back') ? 'back' : 'front';
      const available = variants(product).filter(variant => String(variant.color || '') === name);
      const variant = available.find(value => value.available === true) || available[0];
      const priced = available.filter(value => value.available === true);
      const priceVariants = priced.length ? priced : available;
      const minPrice = priceVariants.length ? Math.min(...priceVariants.map(value => Number(value.price))) : null;
      const varyingPrice = priceVariants.some(value => Number(value.price) !== minPrice);
      const firstImage = (color.images || []).map(imageURL).find(Boolean) ||
        (colors(product).length === 1 ? (product.images || []).map(imageURL).find(Boolean) : '') || '';
      let front = imageURL(entry?.front) || imageURL(faces.front);
      const back = imageURL(entry?.back) || imageURL(faces.back);
      const garment = imageURL(entry?.garment) || (side === 'back' ? back : front) || firstImage;
      const genericView = !front && !back && Boolean(garment);
      if (genericView) {
        front = garment;
        side = 'front';
      }
      const exactStyling = product.lineupStyling?.color === name ? product.lineupStyling : null;
      const model = imageURL(entry?.model) || imageURL(color.modelImage) || imageURL(exactStyling?.image);
      const modelAlt = entry?.modelAlt || imageAlt(color.modelImage) || imageAlt(exactStyling?.image) ||
        (model ? `${product.name} · ${name} am Model. Weitere Kleidung dient dem Styling.` : '');
      const href = new URL(`/products/${encodeURIComponent(product.handle || '')}`, location.origin);
      if (variant) {
        href.searchParams.set('variant', String(variant.id));
        href.searchParams.set('choose_size', '1');
      }
      const result = {
        color: name, side, front, back, garment, model, modelAlt,
        description: summary(product), url: product.handle ? href.pathname + href.search : '',
        price: minPrice !== null ? `${varyingPrice ? 'Ab ' : ''}${formatPrice(minPrice)}` : 'Preis wird bestätigt',
        alphaVerified: entry?.alphaVerified === true, genericView,
        available: variant?.available === true
      };
      if (!mapped[String(product.id)]) mapped[String(product.id)] = {};
      mapped[String(product.id)][name] = {
        front, back, garment, model, modelAlt, printSide: side,
        alphaVerified: result.alphaVerified, genericView
      };
      return result;
    }

    const order = [...items];
    const records = order.map(product => {
      const choices = colors(product).map(color => colorChoice(product, color));
      const wanted = rememberedColors.get(String(product.id)) || preferences[String(product.id)];
      const selected = choices.find(choice => choice.color === wanted) || choices[0] || {
        color: '', side: 'front', front: '', back: '', garment: '', model: '', modelAlt: '',
        description: summary(product), url: '', price: 'Preis wird bestätigt'
      };
      return { product, choices, selected };
    });
    const itemMarkup = ({ product, choices, selected }) => `<button type="button" class="sr-garment" data-sr-item hidden
      data-product-id="${escape(product.id)}" data-title="${escape(product.name)}"
      data-color="${escape(selected.color)}" data-side="${escape(selected.side)}"
      data-front="${escape(selected.front)}" data-back="${escape(selected.back)}"
      data-garment="${escape(selected.garment)}" data-model="${escape(selected.model)}"
      data-model-alt="${escape(selected.modelAlt)}" data-price="${escape(selected.price)}"
      data-description="${escape(selected.description)}" data-url="${escape(selected.url)}"
      aria-hidden="true" tabindex="-1"><span class="sr-turntable"><span class="sr-fabric"></span></span>
      <script type="application/json" data-sr-colors>${json(choices)}</script></button>`;

    rack.innerHTML = `<section class="sr-showroom shopify-section" data-sale-motion data-sale-only="false" data-pieces-motion aria-label="Alle Pieces mit Modelansicht">
      <div class="sr-intro"><p>NLA ORIGINALS</p><h2>THE<br>PIECES.</h2><span>Dein nächstes Statement.</span></div>
      <div class="sr-empty" data-sr-empty hidden><h3>Noch keine Pieces in dieser Auswahl.</h3><p>Wähle eine andere Kategorie oder Kollektion.</p></div>
      <div class="sr-stage" data-sr-stage role="region" aria-roledescription="Karussell" aria-label="Pieces: mit Pfeiltasten oder Wischen wechseln" tabindex="0">
        ${records.map(itemMarkup).join('')}
      </div>
      <div class="sr-caption"><p class="sr-selection-meta" data-sr-price></p><h3 data-sr-title></h3>
        <div class="sr-color-row" data-sr-color-row><label for="${id}-color">Farbe</label><select id="${id}-color" data-sr-color aria-label="Farbe wählen"></select></div>
        <p class="sr-hint">Wischen oder ziehen · zum Öffnen antippen</p>
        <div class="sr-controls"><button type="button" class="sr-icon" data-sr-prev aria-label="Vorheriges Piece">←</button><span data-sr-counter aria-live="polite" aria-atomic="true"></span><button type="button" class="sr-icon" data-sr-next aria-label="Nächstes Piece">→</button></div>
        <button type="button" class="sr-pill" data-sr-open>Piece entdecken <span aria-hidden="true">↗</span></button>
      </div>
      <p class="nla-pieces-motion-note" data-pieces-motion-note></p>
      <dialog class="sr-detail" data-sr-detail aria-labelledby="${id}-title">
        <div class="sr-detail-top"><span>NLA / PIECE DETAIL</span><button type="button" class="sr-close" data-sr-close>Schliessen <span aria-hidden="true">×</span></button></div>
        <div class="sr-detail-image"><img data-sr-detail-image width="900" height="1200" decoding="async" alt=""></div>
        <div class="sr-detail-copy"><p class="sr-detail-count" data-sr-detail-counter></p><h2 id="${id}-title" data-sr-detail-title></h2><p data-sr-detail-description></p>
          <nav class="sr-view-tabs" aria-label="Produktansicht"><button type="button" data-sr-view="front" aria-pressed="false">Vorderseite</button><button type="button" data-sr-view="back" aria-pressed="false">Rückseite</button></nav>
          <p class="sr-detail-status" data-sr-detail-status></p></div>
        <div class="sr-detail-bottom"><span>FARBE & GRÖSSE</span><a href="#" data-sr-buy>Zum Piece <span aria-hidden="true">↗</span></a><span data-sr-draft hidden>Piece wird vorbereitet</span></div>
      </dialog><script type="application/json" data-sr-model-map>${json(mapped)}</script>
    </section>`;
    const root = rack.querySelector('[data-pieces-motion]');
    let disposed = false;

    function sync() {
      if (disposed) return;
      root.querySelectorAll('[data-sr-item]').forEach(item => {
        const fabric = item.querySelector('.sr-fabric');
        if (item.hidden || !item.dataset.garment) {
          if (fabric.firstChild) fabric.replaceChildren();
          return;
        }
        let image = fabric.querySelector('img');
        if (!image) {
          image = document.createElement('img');
          image.alt = '';
          image.draggable = false;
          image.decoding = 'async';
          image.width = 900;
          image.height = 1200;
          fabric.append(image);
        }
        if (image.getAttribute('src') !== item.dataset.garment) image.src = item.dataset.garment;
      });
      const current = root.querySelector('[data-sr-item][aria-current="true"]');
      if (!current) return;
      const productId = current.dataset.productId;
      rememberedColors.set(productId, current.dataset.color || '');
      const entry = own(own(mapped, productId), current.dataset.color);
      const note = root.querySelector('[data-pieces-motion-note]');
      const modelText = entry?.model ? entry.modelAlt : 'Produktansicht · Farbe und Druck des ausgewählten Piece.';
      if (note.textContent !== modelText) note.textContent = modelText;
      const product = byId.get(productId);
      const position = order.findIndex(item => String(item.id) === productId);
      const counter = root.querySelector('[data-sr-counter]');
      if (product && position >= 0) counter.setAttribute('aria-label', `${product.name}, ${position + 1} von ${items.length} Pieces`);
      const detail = root.querySelector('[data-sr-detail]');
      const frontButton = root.querySelector('[data-sr-view="front"]');
      if (frontButton) frontButton.textContent = entry?.genericView ? 'Produktansicht' : 'Vorderseite';
      if (detail?.open && entry?.genericView) {
        root.querySelector('[data-sr-detail-image]').alt = `${current.dataset.title} · ${current.dataset.color} · Produktansicht`;
        const status = root.querySelector('[data-sr-detail-status]');
        status.textContent = `${current.dataset.price} · Produktansicht; Druckseiten sind noch nicht separat verfügbar.`;
      }
    }
    const observer = new MutationObserver(sync);
    observer.observe(root, { subtree: true, attributes: true,
      attributeFilter: ['hidden', 'open', 'data-position', 'data-color', 'data-garment', 'aria-current'] });

    const buy = root.querySelector('[data-sr-buy]');
    const openProduct = event => {
      if (typeof options.open !== 'function' || event.defaultPrevented || event.button > 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const current = root.querySelector('[data-sr-item][aria-current="true"]');
      const product = byId.get(current?.dataset.productId);
      if (!product) return;
      event.preventDefault();
      event.stopPropagation();
      rememberedColors.delete(String(product.id));
      options.open(product, current.dataset.color || '');
    };
    buy.addEventListener('click', openProduct);
    root.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    const initialProduct = String(options.initialProductId || '');
    if (initialProduct) {
      const initialItem = [...root.querySelectorAll('[data-sr-item]')].find(item => item.dataset.productId === initialProduct);
      if (initialItem && initialItem.getAttribute('aria-current') !== 'true') initialItem.click();
    }
    sync();
    return () => {
      if (disposed) return;
      disposed = true;
      observer.disconnect();
      buy.removeEventListener('click', openProduct);
      root.dispatchEvent(new CustomEvent('shopify:section:unload'));
      const detail = root.querySelector('dialog');
      if (detail?.open) detail.close();
      root.querySelectorAll('img').forEach(image => image.removeAttribute('src'));
    };
  }

  window.NLAPiecesMotion = Object.freeze({ version: '20261019.1', mount });
})();
